import { computed, onUnmounted, ref, watch } from 'vue';

type ModalStackEntry = {
  id: number;
  priority: boolean;
};

const stack = ref<ModalStackEntry[]>([]);

let lastId = 0;
let previousOverflow: { html: string; body: string } | undefined;

function updateScrollLock() {
  if (stack.value.length && !previousOverflow) {
    previousOverflow = {
      html: document.documentElement.style.overflow,
      body: document.body.style.overflow,
    };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  } else if (!stack.value.length && previousOverflow) {
    document.documentElement.style.overflow = previousOverflow.html;
    document.body.style.overflow = previousOverflow.body;
    previousOverflow = undefined;
  }
}

export function useModalStack(options: { isActive: () => boolean; priority: () => boolean }) {
  const id = ++lastId;

  const ordered = computed(() =>
    [...stack.value].sort((first, second) => Number(first.priority) - Number(second.priority))
  );

  const isTop = computed(() => ordered.value.at(-1)?.id === id);
  const isBottom = computed(() => ordered.value[0]?.id === id);

  function register() {
    if (stack.value.some((entry) => entry.id === id)) {
      return;
    }

    stack.value.push({ id, priority: options.priority() });
    updateScrollLock();
  }

  function unregister() {
    const index = stack.value.findIndex((entry) => entry.id === id);

    if (index !== -1) {
      stack.value.splice(index, 1);
      updateScrollLock();
    }
  }

  watch(options.isActive, (isActive) => (isActive ? register() : unregister()), { immediate: true });

  onUnmounted(unregister);

  return { isTop, isBottom };
}
